import type {
  PublicRecommendationWithAssets,
  PaginatedRecommendations,
  PublicRecommendationsListParams,
  SavedRecommendation,
  RecentlyViewedEntry,
  RecommendedForYouResult,
  EngagementEvent,
  AuthSession,
  ReferenceWithCount,
  PublicBusinessProfile,
  FunnelEvent,
} from '../types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.losrecomendados.co';
const AUTH_BASE = import.meta.env.VITE_AUTH_BASE_URL || 'https://auth.losrecomendados.co';

const TOKEN_KEY = 'lr-discovery-token';
const USER_KEY = 'lr-discovery-user';

// Client-side view-event dedupe: send a `view` for a recommendation at most
// once per key within the same browser. The server additionally dedupes.
const VIEW_SEEN_KEY = 'lr-discovery-viewed';

function getViewedSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(VIEW_SEEN_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function markViewed(id: string): boolean {
  const set = getViewedSet();
  const firstSeen = !set.has(id);
  set.add(id);
  try {
    localStorage.setItem(VIEW_SEEN_KEY, JSON.stringify([...set]));
  } catch {
    // Storage unavailable — still allow reporting once in-memory.
  }
  return firstSeen;
}

function readStoredUser(): { id: string; email: string; role: string; businessId?: string } | null {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

class DiscoveryApiClient {
  private getAuthToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private setSession(token: string, user: AuthSession['user']) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  /** Refresh the access token. Returns the new token or throws on failure. */
  async refreshToken(): Promise<string> {
    const token = this.getAuthToken();
    if (!token) throw new Error('No session to refresh');
    const response = await fetch(`${AUTH_BASE}/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      this.clearSession();
      throw new Error('La sesión expiró');
    }
    const data = await response.json();
    const user = readStoredUser();
    this.setSession(data.access_token, user ?? { id: '', email: '', role: 'consumer' });
    return data.access_token;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    retried = false,
  ): Promise<T> {
    const token = this.getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401 && this.getAuthToken() && !retried) {
      try {
        await this.refreshToken();
      } catch {
        // Session is truly gone — surface the original error to the caller.
      }
      return this.request<T>(path, options, true);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `Request failed (${response.status})`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Public recommendations (no auth required)
  async getRecommendations(params: PublicRecommendationsListParams = {}): Promise<PaginatedRecommendations> {
    const searchParams = new URLSearchParams();
    if (params.q) searchParams.set('q', params.q);
    if (params.categoryId) searchParams.set('categoryId', params.categoryId);
    if (params.cityId) searchParams.set('cityId', params.cityId);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.cursor) searchParams.set('cursor', params.cursor);

    return this.request<PaginatedRecommendations>(`/v1/public/recommendations?${searchParams.toString()}`);
  }

  async getRecommendation(id: string): Promise<PublicRecommendationWithAssets> {
    return this.request<PublicRecommendationWithAssets>(`/v1/public/recommendations/${id}`);
  }

  async getCategories(): Promise<ReferenceWithCount[]> {
    const body = await this.request<{ categories: ReferenceWithCount[] }>('/v1/public/categories');
    return body.categories;
  }

  async getCities(): Promise<ReferenceWithCount[]> {
    const body = await this.request<{ cities: ReferenceWithCount[] }>('/v1/public/cities');
    return body.cities;
  }

  async getBusinessProfile(businessId: string): Promise<PublicBusinessProfile | null> {
    try {
      return await this.request<PublicBusinessProfile | null>(`/v1/public/businesses/${businessId}`);
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) return null;
      throw err;
    }
  }

  // Auth methods
  async login(email: string, password: string): Promise<AuthSession> {
    const response = await fetch(`${AUTH_BASE}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    const session: AuthSession = {
      token: data.access_token,
      user: data.user,
    };
    this.setSession(session.token, session.user);
    return session;
  }

  async register(email: string, password: string): Promise<AuthSession> {
    const response = await fetch(`${AUTH_BASE}/users/consumer-register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Registration failed' }));
      throw new Error(error.error || 'Registration failed');
    }

    const data = await response.json();
    const session: AuthSession = {
      token: data.access_token,
      user: data.user,
    };
    this.setSession(session.token, session.user);
    return session;
  }

  logout(): void {
    this.clearSession();
  }

  getToken(): string | null {
    return this.getAuthToken();
  }

  getUser(): { id: string; email: string; role: string; businessId?: string } | null {
    return readStoredUser();
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  // Saved recommendations (requires auth)
  async saveRecommendation(recommendationId: string): Promise<{ id: string }> {
    return this.request<{ id: string }>('/v1/consumer/saved', {
      method: 'POST',
      body: JSON.stringify({ recommendationId }),
    });
  }

  async unsaveRecommendation(recommendationId: string): Promise<void> {
    return this.request<void>(`/v1/consumer/saved/${recommendationId}`, {
      method: 'DELETE',
    });
  }

  async getSavedRecommendations(): Promise<SavedRecommendation[]> {
    return this.request<SavedRecommendation[]>('/v1/consumer/saved');
  }

  async getRecentlyViewed(limit = 12): Promise<RecentlyViewedEntry[]> {
    const body = await this.request<{ items: RecentlyViewedEntry[] }>(`/v1/consumer/recently-viewed?limit=${limit}`);
    return body.items;
  }

  async getRecommendedForYou(limit = 12): Promise<RecommendedForYouResult> {
    return this.request<RecommendedForYouResult>(`/v1/consumer/recommended-for-you?limit=${limit}`);
  }

  // Engagement tracking — only `view` and `click` are allowed client-side.
  // Saves are recorded server-side by the saved-recommendation endpoints.
  async trackEvent(event: Omit<EngagementEvent, 'timestamp'>): Promise<void> {
    try {
      if (event.type === 'view' && !markViewed(event.recommendationId)) {
        return;
      }
      await fetch(`${API_BASE}/v1/consumer/engagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.getAuthToken() ? { Authorization: `Bearer ${this.getAuthToken()}` } : {}),
        },
        body: JSON.stringify({
          ...event,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch {
      // Silently fail - engagement tracking shouldn't break UX
    }
  }

  async trackFunnelEvent(event: Omit<FunnelEvent, 'timestamp'>): Promise<void> {
    try {
      await fetch(`${API_BASE}/v1/consumer/engagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.getAuthToken() ? { Authorization: `Bearer ${this.getAuthToken()}` } : {}),
        },
        body: JSON.stringify({
          ...event,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch {
      // Silently fail - funnel tracking shouldn't break UX
    }
  }
}

export const discoveryApi = new DiscoveryApiClient();
export type { DiscoveryApiClient };
